import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Alert, Box, Button, Card, CardContent, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import { useGetBlueprintFamilyMomentumQuery } from '../../api/blueprintFamilies/momentum'
import { useGetBlueprintFamilyRollupQuery } from '../../api/blueprintFamilies/rollup'
import { useGetBlueprintFamilyDensityScalarsQuery } from '../../api/blueprintFamilies/trendsBatch'
import { useGetBlueprintFamilyGroupDensityScalarsQuery } from '../../api/blueprintFamilies/trendsGroupDensity'
import { useGetBlueprintFamiliesQuery } from '../../api/core'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { BlueprintHierarchyBar, BlueprintHierarchyMenus } from '../../features/market-analytics/components/BlueprintHierarchyBar/BlueprintHierarchyBar'
import { BlueprintTrendsChart } from '../../features/market-analytics/components/BlueprintTrendsChart'
import { CatalogueControlsRow } from '../../features/market-analytics/components/CatalogueControlsRow'
import { CatalogueFilterBar } from '../../features/market-analytics/components/CatalogueFilterBar'
import { EventGroupTrendsChart } from '../../features/market-analytics/components/EventGroupTrendsChart'
import { EventTypeTrendsChart } from '../../features/market-analytics/components/EventTypeTrendsChart'
import { MetricLensToggle } from '../../features/market-analytics/components/MetricLensToggle'
import { RankedBlueprintTree } from '../../features/market-analytics/components/RankedBlueprintTree'
import { changeBaselineLabel, changeLatestLabel } from '../../features/market-analytics/components/helpers/adoptionChangeCopy'
import {
  buildCatalogueTree,
  familyEventTypeMemberships,
  buildEventCategoryOptions,
  buildEventCategoryPath,
  buildEventTypePath,
  buildEventTypeOptions,
  collectBlueprintLeaves,
  collectGroupIds,
  DetailPageNavState,
  filterCatalogueToScope,
  withSelectedEventCategoryOptions,
  withSelectedEventTypeOptions,
  buildEventTypeCategoryLookup,
  resolveCohortSize,
  enrichTree,
  eventTypeIdFromNodeId,
  sortNodes,
  buildBlueprintFamilyPath,
  toMomentumMap,
  BLUEPRINT_FAMILY_CHART_TITLE_KEYS,
} from '../../features/market-analytics/components/helpers/catalogueUtils'
import { getEventTypeDescription } from '../../features/market-analytics/components/helpers/eventTypeGlossary'
import { buildHierarchySiblings } from '../../features/market-analytics/components/helpers/hierarchySiblings'
import { getTranslatedName } from '../../features/market-analytics/components/helpers/metricUtils'
import { assignStableSeriesColors } from '../../features/market-analytics/components/helpers/stableSeriesColors'
import { MAX_TRENDS_SERIES } from '../../features/market-analytics/components/helpers/trendsSeries'
import { useBlueprintFamilyDescriptions } from '../../features/market-analytics/hooks/useBlueprintFamilyDescriptions'
import { useCatalogueScope, useCatalogueTaxonomyCounts } from '../../features/market-analytics/hooks/useCatalogueScope'
import { MarketLevelBlueprintListItem } from '../../features/market-analytics/types/MarketLevelBlueprintListItem'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import languageService from '../../services/LanguageService'
import PageService from '../../services/PageService'
import { colors } from '../../themes/theme'
import './MarketAnalyticsPage.scss'

const MarketAnalyticsPage: React.FC = () => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const location = useLocation()

  // The catalogue validates `ecat`/`etype` against the event tag groups; the
  // detail pages don't read those params.
  const {
    state,
    taxonomyQuery: { isError: taxonomyError, refetch: refetchTaxonomy },
    tagGroupsQuery: { isLoading: tagGroupsLoading, isError: tagGroupsError, refetch: refetchTagGroups },
    tagGroups,
    tagGroupInfos,
    windowDays,
    effectiveSubgenreIds,
    conventionalSubgenreIds,
    scope,
    isUnscoped,
    scopePending,
    carriedSearch: detailCarriedSearch,
  } = useCatalogueScope({ validateEventFilters: true })

  usePage(PageService.getPageWithId('market-analytics'), 'Visited Market Analytics')
  useDocumentTitle(t('sidebar:market_analytics'))

  // Until the taxonomy can expand a category / genre pick (`scopePending`), every
  // scoped request below is held rather than fired for the wrong segment and then
  // fired again.
  //
  // The list keeps showing `data` to the filters (so their options don't blink
  // out on every segment change), but the ranked list and charts skeleton while
  // `currentData` is missing — the same rule the detail pages follow — so rows
  // from the previous segment or window are never shown as this one's.
  const {
    data: blueprints,
    currentData: currentBlueprints,
    isLoading,
    isFetching: blueprintsFetching,
    isError: blueprintsError,
    refetch: refetchBlueprints,
  } = useGetBlueprintFamiliesQuery({ windowDays, ...scope }, { skip: scopePending })

  // Taxonomy chip counts come from an unscoped catalogue snapshot so they stay
  // stable while the user filters.
  const { taxonomyCounts } = useCatalogueTaxonomyCounts(blueprints, isUnscoped)

  // Map the API list items to the FE view model. `adoptionRate` is now carried
  // through (previously dropped) so the ranked tree + side rail can use it.
  // Membership is filtered client-side (see `filterCatalogueToScope`), which
  // mirrors the taxonomy chip counts.
  const scopedBlueprints = useMemo(() => filterCatalogueToScope(blueprints, effectiveSubgenreIds) ?? [], [blueprints, effectiveSubgenreIds])

  // One list item per (family, event type) placement. A family carrying several
  // event types is filed under each of them with that type's game count. Category
  // totals come from the deduplicated rollup, so placing a family twice does not
  // double count games at category level.
  //
  // A family with no events inside the window (on mobile, the only platform this list
  // is asked for) has no placement and `flatMap` drops it, so the filter pills, their
  // counts and the tree never list it under a category — even one analysts curated for
  // it (the Data Glossary is where curated categories apply regardless of the window).
  const blueprintData: MarketLevelBlueprintListItem[] = useMemo(() => {
    return scopedBlueprints.flatMap((blueprint) =>
      familyEventTypeMemberships(blueprint).map((membership, index) => ({
        id: blueprint.id,
        placementKey: index === 0 ? blueprint.id : `${blueprint.id}@${membership.typeId}`,
        name: blueprint.names,
        tagGroupId: membership.categoryId,
        tagId: membership.typeId,
        numberOfGames: blueprint.gamesCount ?? 0,
        // Windowed, cohort-scoped adopting games (backend `distinctGamesCount`).
        // Never falls back to the whole-market `gamesCount` above: that count is
        // all-time, so it would read as a much larger adoption figure.
        adoptingGamesInSegment: blueprint.distinctGamesCount ?? null,
        placementAdoptingGames: membership.distinctGamesCount ?? null,
        numberOfLiveEventObjects: blueprint.liveEventsCount ?? 0,
        adoptionRate: blueprint.adoptionRate,
        // Windowed first adopters (`newGames`, driven by the page's `windowDays`).
        // The list only needs how many; the detail pages name them. Complements
        // momentum: absolute wave size vs net change.
        newAdoptersInWindow: blueprint.newGames?.length ?? null,
        discontinued: blueprint.discontinued,
      }))
    )
  }, [scopedBlueprints])

  // Editorial blueprint descriptions for the ranked list's hover tooltips (what
  // the quick-view panel used to explain).
  const blueprintDescriptions = useBlueprintFamilyDescriptions(userLanguage)

  const getBlueprintName = useMemo(() => (bp: MarketLevelBlueprintListItem) => getTranslatedName(bp.name, userLanguage), [userLanguage])
  const getEventTypeName = useMemo(() => (tagId: string) => languageService.getTranslation('tags', tagId) || tagId, [])

  const eventCategoryOptions = useMemo(() => buildEventCategoryOptions(blueprintData, tagGroupInfos), [blueprintData, tagGroupInfos])
  const eventTypeOptions = useMemo(
    () => buildEventTypeOptions(blueprintData, tagGroupInfos, state.eventCategoryIds, getEventTypeName),
    [blueprintData, tagGroupInfos, state.eventCategoryIds, getEventTypeName]
  )

  // A pick no family in this window or segment answers stays applied and keeps its pill, with a
  // count of 0 (see `withSelectedEventCategoryOptions`). `eventTypeOptions` above stays the
  // data-only list: the hierarchy bar reads which type a single pick names from it. While the
  // new window's families are still loading, or the request is held until the segment can be
  // resolved (`scopePending`, which skips it so `isFetching` stays false), that 0 may be stale, so
  // those pills drop the count.
  const pickCountPending = (blueprintsFetching || scopePending) && !currentBlueprints
  const categoryIdByTypeId = useMemo(() => buildEventTypeCategoryLookup(tagGroups), [tagGroups])
  const eventCategoryPills = useMemo(
    () => withSelectedEventCategoryOptions(eventCategoryOptions, state.eventCategoryIds, tagGroupInfos, pickCountPending),
    [eventCategoryOptions, state.eventCategoryIds, tagGroupInfos, pickCountPending]
  )
  const eventTypePills = useMemo(
    () =>
      withSelectedEventTypeOptions(
        eventTypeOptions,
        state.eventTypeIds,
        tagGroupInfos,
        categoryIdByTypeId,
        getEventTypeName,
        state.eventCategoryIds.length > 0,
        pickCountPending
      ),
    [eventTypeOptions, state.eventTypeIds, tagGroupInfos, categoryIdByTypeId, getEventTypeName, state.eventCategoryIds.length, pickCountPending]
  )

  const filter = useMemo(() => ({ eventCategoryIds: state.eventCategoryIds, eventTypeIds: state.eventTypeIds }), [state.eventCategoryIds, state.eventTypeIds])

  // Granularity-driven tree for the ranked list view.
  const tree = useMemo(
    () =>
      buildCatalogueTree({ blueprints: blueprintData, tagGroups: tagGroupInfos, granularity: state.granularity, filter, getBlueprintName, getEventTypeName }),
    [blueprintData, tagGroupInfos, state.granularity, filter, getBlueprintName, getEventTypeName]
  )

  // Windowed live-event density and concurrent event instances per blueprint,
  // sourced from the backend trends batch (the `density` and
  // `activeLiveEventsCount` series) rather than the cumulative liveEventsCount /
  // gamesCount proxy (which over-counts long-ended events and inflated the
  // number, e.g. 63.40 instead of ~3.4). Only fetched when one of those two
  // lenses is active — the endpoint returns monthly series that we boil down to
  // scalars, so it is skipped entirely while viewing adoption. Ids are chunked
  // into ≤10-id requests inside the endpoint to clear the batch cap.
  //
  // The blueprint leaves are collected from the (pre-enrichment) granularity
  // tree rather than a second `buildCatalogueTree` pass — the leaf set is the
  // same at every granularity, only the grouping differs. Ids are sorted so the
  // RTK cache key stays stable across granularity changes.
  const scalarLensActive = state.listSort !== 'adoption'
  const scopedLeafIds = useMemo(() => Array.from(new Set(collectBlueprintLeaves(tree).map((leaf) => leaf.blueprintId ?? leaf.id))).sort(), [tree])
  const {
    currentData: trendScalars,
    isFetching: scalarsFetching,
    refetch: refetchTrendScalars,
  } = useGetBlueprintFamilyDensityScalarsQuery(
    { ids: scopedLeafIds, windowDays, ...scope },
    { skip: scopePending || !scalarLensActive || scopedLeafIds.length === 0 }
  )
  const densityByBlueprintId = useMemo(() => new Map((trendScalars ?? []).map((row) => [row.id, row.density])), [trendScalars])
  const activeEventsByBlueprintId = useMemo(() => new Map((trendScalars ?? []).map((row) => [row.id, row.activeEvents])), [trendScalars])
  // First and last month of each density series, for the Change column on the density lens.
  const densityChangeByBlueprintId = useMemo(() => new Map((trendScalars ?? []).map((row) => [row.id, row.densityChange])), [trendScalars])

  // Category and event-type rows read their own trends series for density, the same
  // figure their detail pages show. Summing the child families instead would add
  // per-family averages that each cover a different set of games.
  const { eventGroupIds: groupDensityGroupIds, eventTypeIds: groupDensityTypeIds } = useMemo(() => collectGroupIds(tree), [tree])
  const {
    currentData: groupDensityScalars,
    isFetching: groupDensityFetching,
    refetch: refetchGroupDensityScalars,
  } = useGetBlueprintFamilyGroupDensityScalarsQuery(
    { eventGroupIds: groupDensityGroupIds, eventTypeIds: groupDensityTypeIds, windowDays, ...scope },
    { skip: scopePending || !scalarLensActive || (groupDensityGroupIds.length === 0 && groupDensityTypeIds.length === 0) }
  )
  const densityByGroupId = useMemo(() => new Map((groupDensityScalars ?? []).map((row) => [row.id, row.density])), [groupDensityScalars])
  const densityChangeByGroupId = useMemo(() => new Map((groupDensityScalars ?? []).map((row) => [row.id, row.densityChange])), [groupDensityScalars])

  // A row whose chunk failed carries null figures that mean "unknown", not "no data",
  // yet ranks with the dashes at the bottom. Say so, and retry just the query that has
  // failed rows (a skipped query can't be refetched).
  const blueprintDensityFailed = scalarLensActive && !!trendScalars?.some((row) => row.failed)
  const groupDensityFailed = scalarLensActive && !!groupDensityScalars?.some((row) => row.failed)
  const hasDensityRowFailures = blueprintDensityFailed || groupDensityFailed
  const retryFailedDensityRows = useCallback(() => {
    if (blueprintDensityFailed) refetchTrendScalars()
    if (groupDensityFailed) refetchGroupDensityScalars()
  }, [blueprintDensityFailed, refetchTrendScalars, groupDensityFailed, refetchGroupDensityScalars])

  // Windowed adoption + change for the ranked list. The rollup endpoint reports
  // distinct adopting games, adoption %, and the windowed `newGames` per event
  // group / event type; we merge it into the tree's group nodes and derive
  // per-blueprint adoption from each leaf's games count + the cohort size.
  const {
    currentData: groupRollup,
    isFetching: groupRollupFetching,
    isError: groupRollupError,
  } = useGetBlueprintFamilyRollupQuery(
    {
      groupBy: 'EVENT_GROUP',
      windowDays,
      ...scope,
    },
    { skip: scopePending }
  )
  // A flat blueprint-granularity tree has no event-type nodes to enrich, so the
  // EVENT_TYPE rollup request is skipped there (EVENT_GROUP must stay — it also
  // supplies the cohort size used by the leaves).
  const { currentData: typeRollup } = useGetBlueprintFamilyRollupQuery(
    {
      groupBy: 'EVENT_TYPE',
      windowDays,
      ...scope,
    },
    { skip: scopePending || state.granularity === 'blueprint' }
  )

  // Chart mode renders trend series instead of the ranked list; momentum and the
  // "Change" column it powers are list-only, so its query (and enrichment cost)
  // are skipped here — chart sorting uses adoption/density, not momentum.
  const chartMode = state.viewMode === 'chart'

  // Per-blueprint momentum (net change in adopting games vs the previous window)
  // powers the ranked list's "Change" column — folding what used to be a
  // separate momentum card at the bottom of the page into the table itself.
  const { currentData: momentumData } = useGetBlueprintFamilyMomentumQuery({ windowDays, ...scope }, { skip: scopePending || chartMode })
  const momentumByBlueprintId = useMemo(() => toMomentumMap(momentumData), [momentumData])

  // Group-level change for the same column. The endpoint dedupes games across the
  // whole group, which summing child momentum cannot do — a game adopting two
  // families in the window would be counted twice.
  //
  // Both groupings are fetched, mirroring the rollup above, because a tree can show
  // more tiers than its granularity names: at event-category granularity the rows
  // run category → event type → blueprint, so fetching only EVENT_GROUP left every
  // event-type row with a permanent dash in a column its neighbours above and below
  // both filled. Each grouping is skipped only where that tier can't be on screen.
  const { currentData: categoryMomentumData } = useGetBlueprintFamilyMomentumQuery(
    { groupBy: 'EVENT_GROUP', windowDays, ...scope },
    { skip: scopePending || chartMode || state.granularity !== 'eventCategory' }
  )
  const { currentData: typeMomentumData } = useGetBlueprintFamilyMomentumQuery(
    { groupBy: 'EVENT_TYPE', windowDays, ...scope },
    { skip: scopePending || chartMode || state.granularity === 'blueprint' }
  )
  // Category ids and event-type ids never collide, so one map serves both tiers —
  // which is what enrichTree expects.
  const groupMomentumByGroupId = useMemo(
    () => new Map([...toMomentumMap(categoryMomentumData), ...toMomentumMap(typeMomentumData)]),
    [categoryMomentumData, typeMomentumData]
  )

  const cohortSize = useMemo(() => resolveCohortSize(groupRollup ?? []), [groupRollup])
  const groupRollupMap = useMemo(() => new Map((groupRollup ?? []).map((row) => [row.groupId, row])), [groupRollup])
  const typeRollupMap = useMemo(() => new Map((typeRollup ?? []).map((row) => [row.groupId, row])), [typeRollup])
  // Single-pass enrichment: fold rollup adoption, per-blueprint momentum, and the
  // backend windowed density onto the tree in one traversal so each data change
  // rebuilds node identities once (not 2-3×). The ranked list's Density column +
  // sort use the windowed density instead of the cumulative proxy.
  const densityEnrichedTree = useMemo(
    () =>
      enrichTree(tree, {
        rollupMaps: { group: groupRollupMap, type: typeRollupMap },
        densityMap: densityByBlueprintId,
        groupDensityMap: densityByGroupId,
        densityChangeMap: densityChangeByBlueprintId,
        groupDensityChangeMap: densityChangeByGroupId,
        activeEventsMap: activeEventsByBlueprintId,
        momentumMap: momentumByBlueprintId,
        groupMomentumMap: groupMomentumByGroupId,
        cohortSize,
      }),
    [
      tree,
      groupRollupMap,
      typeRollupMap,
      densityByBlueprintId,
      densityByGroupId,
      densityChangeByBlueprintId,
      densityChangeByGroupId,
      activeEventsByBlueprintId,
      momentumByBlueprintId,
      groupMomentumByGroupId,
      cohortSize,
    ]
  )

  // Chart series inputs — only computed in chart mode (the list view never
  // reads them, so list-mode data changes skip these sorts entirely).
  //
  // Up to 10 series per batched trends request (endpoint cap). The chart slices
  // again defensively, but we keep the inputs tidy here. The blueprint leaves
  // come from the enriched tree, which already carries density + momentum.
  //
  // Series colors follow the metric-independent adoption rank (see the `*ColorById`
  // memos below), so toggling the lens reorders the lines without recoloring them.
  // The items carry no color — only the glossary copy the chart legend shows on hover.
  const blueprintChartItems = useMemo(() => {
    if (!chartMode) return []
    return sortNodes(collectBlueprintLeaves(densityEnrichedTree), state.listSort).map((leaf) => {
      const id = leaf.blueprintId ?? leaf.id
      return { id, name: leaf.name, description: blueprintDescriptions.get(id) }
    })
  }, [chartMode, densityEnrichedTree, state.listSort, blueprintDescriptions])
  // Event-group series come from the top-level category nodes when grouping by
  // event category (node.id is the event-group / tagGroup id).
  const eventGroupChartItems = useMemo(() => {
    if (!chartMode) return []
    return sortNodes(densityEnrichedTree, state.listSort)
      .filter((node) => node.kind === 'eventCategory')
      .map((node) => ({ id: node.id, name: node.name }))
  }, [chartMode, densityEnrichedTree, state.listSort])
  // Event-type series, the same way, from the top-level nodes of an event-type tree.
  // Their node ids are composite ("<groupId>:<typeId>") so the tree can hold the same
  // type under two categories; the endpoint wants the bare type id.
  const eventTypeChartItems = useMemo(() => {
    if (!chartMode) return []
    return sortNodes(densityEnrichedTree, state.listSort)
      .filter((node) => node.kind === 'eventType')
      .map((node) => {
        const id = eventTypeIdFromNodeId(node.id)
        return { id, name: node.name, description: getEventTypeDescription(id) || undefined, isPlainTextDescription: true }
      })
  }, [chartMode, densityEnrichedTree, state.listSort])

  // Colors are assigned to the plotted top series from their adoption rank, whatever
  // metric sorts the chart. Only the active granularity's chart is mounted, so only
  // its colors are computed.
  const blueprintColorById = useMemo(() => {
    if (!chartMode || state.granularity !== 'blueprint') return undefined
    const preferredIds = sortNodes(collectBlueprintLeaves(densityEnrichedTree), 'adoption').map((leaf) => leaf.blueprintId ?? leaf.id)
    const plottedIds = blueprintChartItems.slice(0, MAX_TRENDS_SERIES).map((item) => item.id)
    return assignStableSeriesColors(plottedIds, preferredIds, colors.chartSeries)
  }, [chartMode, state.granularity, densityEnrichedTree, blueprintChartItems])
  const eventGroupColorById = useMemo(() => {
    if (!chartMode || state.granularity !== 'eventCategory') return undefined
    const preferredIds = sortNodes(densityEnrichedTree, 'adoption')
      .filter((node) => node.kind === 'eventCategory')
      .map((node) => node.id)
    const plottedIds = eventGroupChartItems.slice(0, MAX_TRENDS_SERIES).map((item) => item.id)
    return assignStableSeriesColors(plottedIds, preferredIds, colors.chartSeries)
  }, [chartMode, state.granularity, densityEnrichedTree, eventGroupChartItems])
  const eventTypeColorById = useMemo(() => {
    if (!chartMode || state.granularity !== 'eventType') return undefined
    const preferredIds = sortNodes(densityEnrichedTree, 'adoption')
      .filter((node) => node.kind === 'eventType')
      .map((node) => eventTypeIdFromNodeId(node.id))
    const plottedIds = eventTypeChartItems.slice(0, MAX_TRENDS_SERIES).map((item) => item.id)
    return assignStableSeriesColors(plottedIds, preferredIds, colors.chartSeries)
  }, [chartMode, state.granularity, densityEnrichedTree, eventTypeChartItems])

  // When an event type is selected the segment narrows below event-category, so
  // bump granularity off the now-locked event-category grouping, and hand it back
  // once the last type is deselected (the event-category button unlocks then). Only
  // a bump made here is undone: an event-type granularity the user picked (or a
  // shared link carries) stays put. All three deps are identity-stable (the hook
  // memoizes per URL param), so this only runs on real changes.
  const { eventTypeIds: stateEventTypeIds, granularity: stateGranularity, setGranularity: stateSetGranularity } = state
  const granularityBumpedByTypeRef = useRef(false)
  useEffect(() => {
    if (stateEventTypeIds.length > 0) {
      if (stateGranularity === 'eventCategory') {
        granularityBumpedByTypeRef.current = true
        stateSetGranularity('eventType')
      } else if (stateGranularity !== 'eventType') {
        granularityBumpedByTypeRef.current = false
      }
      return
    }
    if (granularityBumpedByTypeRef.current && stateGranularity === 'eventType') {
      stateSetGranularity('eventCategory')
    }
    granularityBumpedByTypeRef.current = false
  }, [stateEventTypeIds, stateGranularity, stateSetGranularity])

  const showSkeletons = isLoading || tagGroupsLoading
  // The list and charts also skeleton while a new segment / window is fetching
  // and only the previous one's rows are cached. The EVENT_GROUP rollup counts
  // too: it supplies the adoption figures and the cohort every row divides by.
  const listRefreshing = (blueprintsFetching && !currentBlueprints) || (groupRollupFetching && !groupRollup)
  // Skeleton the ranked list on the first scalar fetch so the Density /
  // Concurrent instances column never flashes "—" before the backend scalars
  // land (cached windows resolve instantly and skip this).
  const scalarsInitialLoading =
    scalarLensActive && ((scalarsFetching && trendScalars === undefined) || (groupDensityFetching && groupDensityScalars === undefined))
  const listLoading = showSkeletons || scopePending || listRefreshing

  // A failed catalogue / tag-group request would otherwise leave `blueprintData`
  // empty and the ranked list would show its "no results" empty state — telling
  // the user their filters matched nothing when the API actually failed. Render
  // a dedicated error card with a retry instead. A taxonomy failure on its own
  // only degrades the filter bar (which offers its own retry), unless a category
  // or genre pick is waiting on it to know which games to ask for.
  const hasLoadError = blueprintsError || tagGroupsError || (taxonomyError && scopePending)
  const handleRetryLoad = useCallback(() => {
    if (blueprintsError) refetchBlueprints()
    if (tagGroupsError) refetchTagGroups()
    if (taxonomyError) refetchTaxonomy()
  }, [blueprintsError, refetchBlueprints, tagGroupsError, refetchTagGroups, taxonomyError, refetchTaxonomy])

  // The metric lens (Adoption / Event density / Concurrent event instances) is
  // one URL-backed state shared by the list column and the chart series. It is
  // rendered inside the header of whichever card is showing — the toggle sits on
  // the thing it changes — instead of in the control row above.
  const metricLensToggle = useMemo(() => <MetricLensToggle value={state.listSort} onChange={state.setListSort} />, [state.listSort, state.setListSort])

  // "Open full details" carries the active taxonomy filters + window to the
  // detail page as `detailCarriedSearch` (same `category`/`genre`/`subgenre`/`win`
  // params, parsed by the same URL-state hook there), so the segment scope
  // survives the navigation.
  //
  // That URL only carries taxonomy + window (so it stays clean and
  // shareable); the *full* current search — event filters, granularity, view,
  // sort, expansion — rides along in router navigation state so the detail
  // page's breadcrumb can restore this exact catalogue view on the way back. The
  // per-row momentum value is attached by the tree (each leaf carries its own).
  const detailNavState = useMemo<DetailPageNavState>(
    () => ({ catalogueBackSearch: location.search, deltaWindowDays: windowDays }),
    [location.search, windowDays]
  )

  // Which category / event type the event filters narrow the catalogue to, if they
  // unambiguously name one (a multi-select describes no single path). It never
  // names or highlights a crumb — see the bar below — it only decides which
  // sibling menus are worth offering.
  const narrowedTo = useMemo(() => {
    const singleEventType = state.eventTypeIds.length === 1 ? eventTypeOptions.find((option) => option.id === state.eventTypeIds[0]) : undefined
    return {
      eventTypeId: singleEventType?.id,
      categoryId: state.eventCategoryIds.length === 1 ? state.eventCategoryIds[0] : singleEventType?.categoryId,
    }
  }, [state.eventTypeIds, state.eventCategoryIds, eventTypeOptions])

  // The hierarchy bar on the catalogue is pure navigation, never a mirror of the
  // filters: "Overview" stays the current step however the pills below are set,
  // and the lower levels stay placeholders. Every crumb menu leads straight to the
  // chosen level's own page (the same page its ranked-list row opens); narrowing
  // the Overview itself is what the filter pills are for. Event types are only
  // offered once a category narrows the list, and blueprints once an event type
  // does — a menu of every one in the market is not scannable.
  const hierarchyMenus = useMemo<BlueprintHierarchyMenus>(() => {
    const siblings = buildHierarchySiblings(scopedBlueprints, {
      categoryId: narrowedTo.categoryId,
      eventTypeId: narrowedTo.eventTypeId,
      userLanguage,
      tagGroups: tagGroupInfos,
    })

    return {
      eventCategory: {
        ariaLabel: t('market-analytics:hierarchy_menu_event_categories'),
        options: siblings.eventCategories.map((sibling) => ({
          key: sibling.id,
          label: sibling.label,
          to: buildEventCategoryPath(sibling.id, detailCarriedSearch),
          state: detailNavState,
          colorHex: sibling.colorHex,
        })),
      },
      eventType: narrowedTo.categoryId
        ? {
            ariaLabel: t('market-analytics:hierarchy_menu_event_types'),
            options: siblings.eventTypes.map((sibling) => ({
              key: sibling.id,
              label: sibling.label,
              to: buildEventTypePath(sibling.id, detailCarriedSearch),
              state: detailNavState,
              colorHex: sibling.colorHex,
            })),
          }
        : undefined,
      blueprint: narrowedTo.eventTypeId
        ? {
            ariaLabel: t('market-analytics:hierarchy_menu_blueprints'),
            options: siblings.blueprints.map((sibling) => ({
              key: sibling.id,
              label: sibling.label,
              to: buildBlueprintFamilyPath(sibling.id, detailCarriedSearch),
              state: detailNavState,
              colorHex: sibling.colorHex,
            })),
          }
        : undefined,
    }
  }, [scopedBlueprints, narrowedTo, userLanguage, detailCarriedSearch, detailNavState, tagGroupInfos, t])

  return (
    <Box component="main" className="MarketAnalyticsPage" aria-labelledby="market-analytics-page-title">
      <Typography id="market-analytics-page-title" component="h1" sx={visuallyHidden}>
        {t('sidebar:market_analytics')}
      </Typography>

      {/* Persistent hierarchy bar — the same trail the detail pages show, with
          grayed-out placeholders for the levels the current filters haven't
          resolved yet. The blueprint level is always a placeholder here; its
          menu is the shortcut into a blueprint's own page. */}
      <BlueprintHierarchyBar isCatalogueRoot menus={hierarchyMenus} isLoading={showSkeletons} />

      <CatalogueFilterBar
        selectedCategoryIds={state.categoryGenre.conventionalCategoryIds}
        selectedGenreIds={state.categoryGenre.conventionalGenreIds}
        selectedSubgenreIds={state.categoryGenre.conventionalSubgenreIds}
        onSetCategories={state.setCategories}
        onSetGenres={state.setGenres}
        onSetSubgenres={state.setSubgenres}
        onClearCategories={state.clearCategories}
        onClearGenres={state.clearGenres}
        onClearSubgenres={state.clearSubgenres}
        onClearTaxonomy={state.clearTaxonomy}
        taxonomyCounts={taxonomyCounts}
        cohortGamesCount={cohortSize}
        isCohortLoading={scopePending || (!groupRollup && !groupRollupError)}
        eventCategoryOptions={eventCategoryPills}
        eventTypeOptions={eventTypePills}
        selectedEventCategoryIds={state.eventCategoryIds}
        selectedEventTypeIds={state.eventTypeIds}
        onToggleEventCategory={state.toggleEventCategory}
        onToggleEventType={state.toggleEventType}
        onClearEventCategories={state.clearEventCategories}
        onClearEventTypes={state.clearEventTypes}
        onClearEvents={state.clearEvents}
        isLoading={showSkeletons}
      />

      {hasLoadError ? (
        /* Page-blocking load failure: shown instead of the catalogue so a failed
           request is never mistaken for a valid zero-result filter state. */
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h3" gutterBottom>
              {t('market-analytics:catalogue_load_error')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('market-analytics:catalogue_load_error_description')}
            </Typography>
            <Button variant="contained" color="primary" onClick={handleRetryLoad}>
              {t('market-analytics:retry')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Catalogue list / chart, full width — blueprint details now live on
          their own page instead of a rail beside the list. */
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Control row, kept above the list card. */}
          <CatalogueControlsRow
            viewMode={state.viewMode}
            onViewModeChange={state.setViewMode}
            granularity={state.granularity}
            onGranularityChange={state.setGranularity}
            eventCategoryLocked={state.eventTypeIds.length > 0}
            window={state.window}
            onWindowChange={state.setWindow}
          />

          {hasDensityRowFailures ? (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={retryFailedDensityRows}>
                  {t('market-analytics:retry')}
                </Button>
              }
            >
              {t('market-analytics:detail_partial_load_error')}
            </Alert>
          ) : null}

          {/* List / chart */}
          <Box sx={{ minWidth: 0 }}>
            {state.viewMode === 'list' ? (
              <RankedBlueprintTree
                nodes={densityEnrichedTree}
                metric={state.listSort}
                granularity={state.granularity}
                expandedIds={state.expandedNodeIds}
                onToggleExpand={state.toggleExpandedNode}
                sortKey={state.treeSortKey}
                sortDir={state.treeSortDir}
                onSortChange={state.setTreeSort}
                detailSearch={detailCarriedSearch}
                detailNavState={detailNavState}
                descriptionsByBlueprintId={blueprintDescriptions}
                getEventTypeDescription={getEventTypeDescription}
                changeBaselineLabel={changeBaselineLabel(state.window, userLanguage)}
                changeLatestLabel={changeLatestLabel(state.window, userLanguage)}
                headerControls={metricLensToggle}
                isLoading={listLoading || scalarsInitialLoading}
              />
            ) : state.granularity === 'eventCategory' ? (
              // Each chart plots the selected period's calendar months (see
              // `selectedPeriodFor`).
              <EventGroupTrendsChart
                groups={eventGroupChartItems}
                colorById={eventGroupColorById}
                metric={state.listSort}
                windowDays={windowDays}
                conventionalSubgenreIds={conventionalSubgenreIds}
                isLoading={listLoading}
                tabs={metricLensToggle}
              />
            ) : state.granularity === 'eventType' ? (
              // Real type-level lines now that `trends/event-types` exists. This used
              // to fall through to the blueprint chart below with a notice explaining
              // that the lines were the types' member families rather than the types.
              <EventTypeTrendsChart
                types={eventTypeChartItems}
                colorById={eventTypeColorById}
                metric={state.listSort}
                windowDays={windowDays}
                conventionalSubgenreIds={conventionalSubgenreIds}
                isLoading={listLoading}
                tabs={metricLensToggle}
              />
            ) : (
              <BlueprintTrendsChart
                items={blueprintChartItems}
                colorById={blueprintColorById}
                title={t(BLUEPRINT_FAMILY_CHART_TITLE_KEYS[state.listSort])}
                metric={state.listSort}
                windowDays={windowDays}
                conventionalSubgenreIds={conventionalSubgenreIds}
                isLoading={listLoading}
                tabs={metricLensToggle}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default MarketAnalyticsPage
